import type { BootstrapConfig } from '../api/types';
import type { DealerSummary } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import { fetchNearbyDealers } from '../api/client';

/**
 * Step — Dealer Select
 *
 * Lets the customer find and choose a dealer near their zip code before
 * submitting the quote.  Only shown when the tenant has `enableDealerPortal`
 * turned on in Settings.  Choosing a dealer is optional — the customer may
 * skip with "Continue without a dealer".
 */
export class StepDealerSelect implements BuilderStep {
    readonly id = 'dealer-select';
    readonly label = 'Choose a Dealer';

    private container: HTMLElement | null = null;

    constructor(
        private store: BuilderStore,
        public readonly config: BootstrapConfig
    ) { }

    isEnabled(config: BootstrapConfig): boolean {
        return config.settings['enableDealerPortal'] === true;
    }

    render(container: HTMLElement): void {
        this.container = container;
        // Pre-fill postal code from customer info if already entered
        const postalCode = this.store.getState().quoteData.postalCode ?? '';
        const selectedId = this.store.getState().selectedDealerId;

        container.innerHTML = `
      <div class="vc-step vc-step-dealer-select">
        <h2>Find a Dealer Near You</h2>
        <p class="vc-step-desc">
          Enter your postal code to find an authorized dealer in your area.
          Selecting a dealer is optional — they will receive a copy of your quote and follow up with you directly.
        </p>

        <div class="vc-dealer-search-bar">
          <div class="vc-form-row vc-dealer-search-row">
            <div class="vc-form-group vc-dealer-zip-group">
              <label for="vc-dealer-zip">Postal / ZIP Code</label>
              <input
                type="text"
                id="vc-dealer-zip"
                class="vc-input"
                placeholder="e.g. 90210"
                value="${postalCode}"
                maxlength="10"
              />
            </div>
            <div class="vc-form-group vc-dealer-country-group">
              <label for="vc-dealer-country">Country</label>
              <select id="vc-dealer-country" class="vc-select">
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="vc-form-group vc-dealer-search-btn-group">
              <label>&nbsp;</label>
              <button id="vc-dealer-search-btn" class="vc-btn vc-btn-primary">Search</button>
            </div>
          </div>
        </div>

        <div id="vc-dealer-results" class="vc-dealer-results" role="radiogroup" aria-label="Dealer selection"></div>

        <div id="vc-dealer-message" class="vc-form-message"></div>
      </div>
    `;

        container.querySelector('#vc-dealer-search-btn')
            ?.addEventListener('click', () => this.handleSearch());

        container.querySelector('#vc-dealer-zip')
            ?.addEventListener('keydown', (e) => {
                if ((e as KeyboardEvent).key === 'Enter') this.handleSearch();
            });

        // If we already have a selectedDealerId or a postal code, auto-search
        if (postalCode) {
            this.handleSearch();
        } else if (selectedId !== null) {
            // Already selected from a previous visit — show current selection
            this.renderSingleSelection(selectedId);
        }
    }

    validate(): boolean {
        // Dealer selection is optional; the step is always passable
        return true;
    }

    onEnter(): void { }

    onLeave(): void { }

    destroy(): void {
        this.container = null;
    }

    // ── Private ─────────────────────────────────────────────

    private async handleSearch(): Promise<void> {
        const zipInput = this.container?.querySelector('#vc-dealer-zip') as HTMLInputElement | null;
        const countrySelect = this.container?.querySelector('#vc-dealer-country') as HTMLSelectElement | null;
        const resultsEl = this.container?.querySelector('#vc-dealer-results') as HTMLElement | null;
        const msgEl = this.container?.querySelector('#vc-dealer-message') as HTMLElement | null;
        const searchBtn = this.container?.querySelector('#vc-dealer-search-btn') as HTMLButtonElement | null;

        const zip = zipInput?.value.trim() ?? '';
        const country = countrySelect?.value ?? 'US';

        if (!zip) {
            if (msgEl) msgEl.innerHTML = '<div class="vc-error">Please enter a postal code.</div>';
            return;
        }

        if (msgEl) msgEl.innerHTML = '';
        if (resultsEl) resultsEl.innerHTML = '<p class="vc-loading">Searching…</p>';
        if (searchBtn) {
            searchBtn.disabled = true;
            searchBtn.textContent = 'Searching…';
        }

        try {
            const dealers = await fetchNearbyDealers(zip, country);
            this.renderDealerList(dealers, resultsEl);
        } catch {
            if (msgEl) msgEl.innerHTML = '<div class="vc-error">Unable to load dealers. Please try again.</div>';
            if (resultsEl) resultsEl.innerHTML = '';
        } finally {
            if (searchBtn) {
                searchBtn.disabled = false;
                searchBtn.textContent = 'Search';
            }
        }
    }

    private renderDealerList(dealers: DealerSummary[], resultsEl: HTMLElement | null): void {
        if (!resultsEl) return;

        const selectedId = this.store.getState().selectedDealerId;

        if (dealers.length === 0) {
            resultsEl.innerHTML = `
        <p class="vc-dealer-none">No dealers found in your area. You may continue without selecting a dealer.</p>
        ${this.buildSkipOption(selectedId)}
      `;
            this.attachResultsListeners(resultsEl);
            return;
        }

        const items = dealers.map(d => {
            const checked = selectedId === d.id ? 'checked' : '';
            const locationParts = [d.city, d.state_province, d.postal_code].filter(Boolean);
            const location = locationParts.join(', ');
            const phone = d.phone ? `<span class="vc-dealer-phone">${d.phone}</span>` : '';
            const website = d.website
                ? `<a class="vc-dealer-website" href="${d.website}" target="_blank" rel="noopener noreferrer">Website</a>`
                : '';

            return `
        <label class="vc-dealer-option ${checked ? 'vc-dealer-option--selected' : ''}" data-dealer-id="${d.id}">
          <input type="radio" name="vc_dealer" value="${d.id}" ${checked} class="vc-dealer-radio" />
          <span class="vc-dealer-info">
            <span class="vc-dealer-name">${d.dealer_name}</span>
            ${location ? `<span class="vc-dealer-location">${location}</span>` : ''}
            <span class="vc-dealer-contact">${phone}${phone && website ? ' · ' : ''}${website}</span>
          </span>
        </label>
      `;
        }).join('');

        resultsEl.innerHTML = `
      <div class="vc-dealer-list">
        ${items}
        ${this.buildSkipOption(selectedId)}
      </div>
    `;

        this.attachResultsListeners(resultsEl);
    }

    private buildSkipOption(selectedId: number | null): string {
        const checked = selectedId === 0 ? 'checked' : '';
        return `
      <label class="vc-dealer-option vc-dealer-option--skip ${checked ? 'vc-dealer-option--selected' : ''}" data-dealer-id="0">
        <input type="radio" name="vc_dealer" value="0" ${checked} class="vc-dealer-radio" />
        <span class="vc-dealer-info">
          <span class="vc-dealer-name">Continue without a dealer</span>
          <span class="vc-dealer-location">No dealer will be assigned to your quote</span>
        </span>
      </label>
    `;
    }

    private attachResultsListeners(resultsEl: HTMLElement): void {
        resultsEl.querySelectorAll<HTMLInputElement>('input[name="vc_dealer"]').forEach(radio => {
            radio.addEventListener('change', () => {
                const val = parseInt(radio.value, 10);
                // 0 = skip, any other positive int = dealer id
                this.store.setState({ selectedDealerId: val === 0 ? null : val });

                // Update visual selected state
                resultsEl.querySelectorAll('.vc-dealer-option').forEach(el => {
                    el.classList.toggle('vc-dealer-option--selected',
                        el.getAttribute('data-dealer-id') === radio.value);
                });
            });
        });
    }

    /** Called when re-entering the step and a dealer is already selected — show a brief summary. */
    private renderSingleSelection(dealerId: number): void {
        const resultsEl = this.container?.querySelector('#vc-dealer-results') as HTMLElement | null;
        if (!resultsEl) return;
        resultsEl.innerHTML = `
      <p class="vc-dealer-selected-hint">
        Dealer #${dealerId} selected. Enter a postal code above to search again.
      </p>
      ${this.buildSkipOption(null)}
    `;
        this.attachResultsListeners(resultsEl);
    }
}
